import {
  Box,
  Button,
  Container,
  Divider,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Features from "./features";
import { uploadImage } from "services/imageUpload";
import { IMAGE_URL } from "network";
import {
  useAddConcernMutation,
  useUpdateConcernMutation,
  useGetOneConcernQuery,
} from "slices/wellness/concernSlice";
// import { skinConditionDescriptions } from "utility/data";
import { useGetResponseConcernQuery } from "slices/wellness/concernSlice";

function AddConcern() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data: initialData, refetch } = useGetOneConcernQuery(id, {
    skip: !isEdit,
  });
  const { data: skinConditionDescriptions  } = useGetResponseConcernQuery({});
  const [add] = useAddConcernMutation();
  const [update] = useUpdateConcernMutation();

  const [previewUrl, setPreviewUrl] = useState("");
  const [iconPreviewUrl, setIconPreviewUrl] = useState("");

  console.log("initialData", initialData);

  // useEffect(() => {
  //   if (isEdit) {
  //     const hasReloaded = sessionStorage.getItem("concernEditReloaded");

  //     if (!hasReloaded) {
  //       sessionStorage.setItem("concernEditReloaded", "true");
  //       window.location.reload();
  //     }
  //   }
  // }, [isEdit]);

  // useEffect(() => {
  //   return () => {
  //     sessionStorage.removeItem("concernEditReloaded");
  //   };
  // }, []);

  useEffect(() => {
    if (initialData) {
      if (initialData?.image) {
        setPreviewUrl(IMAGE_URL + initialData?.image);
      }
      if (initialData?.icon) {
        setIconPreviewUrl(IMAGE_URL + initialData?.icon);
      }
    }
  }, [initialData]);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    linked: Yup.string().required("This is required"),
    icon: Yup.string().test(
      "icon-required",
      "Icon is required",
      function (value) {
        if (!isEdit || !initialData?.icon) {
          return Boolean(value);
        }
        return true;
      }
    ),
    image: Yup.string().test(
      "image-required",
      "Image is required",
      function (value) {
        if (!isEdit || !initialData?.image) {
          return Boolean(value);
        }
        return true;
      }
    ),
    features: Yup.array()
      .of(
        Yup.object().shape({
          title: Yup.string().required("Title is required"),
          description: Yup.string().required("Description is required"),
          treatments: Yup.array().min(1, "Add at least one treatment"),
        })
      )
      .min(1, "Add at least one feature")
      .required("Add at least one feature"),
  });

  const initialValues = {
    title: initialData?.title || "",
    description: initialData?.description || "",
    icon: initialData?.icon || "",
    image: initialData?.image || "",
    features: initialData?.features || [],
    linked: initialData?.linked || "",
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h4" gutterBottom>
          {initialData ? "Edit Concern" : "Add Concern"}
        </Typography>

        <Paper elevation={2} sx={{ p: 3 }}>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                const payload = { ...values };
                if (isEdit) {
                  let res = await update({ id, body: payload }).unwrap();
                  if (res) {
                    await refetch();
                    navigate("/wellness/concerns");
                  }
                } else {
                  let res = await add(payload).unwrap();
                  if (res) {
                    navigate("/wellness/concerns");
                  }
                }
              } catch (error) {
                console.error("Submission error:", error);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue, isSubmitting }) => (
              <Form>
                {/* Icon Upload */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Upload Icon
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const iconName = await uploadImage(file);
                      setFieldValue("icon", iconName);
                      setIconPreviewUrl(IMAGE_URL + iconName);
                    }
                  }}
                />
                {iconPreviewUrl && (
                  <Box mt={2}>
                    <img
                      crossOrigin="anonymous"
                      src={iconPreviewUrl}
                      alt="Icon Preview"
                      style={{ width: 140, borderRadius: 8 }}
                    />
                  </Box>
                )}
                <ErrorMessage
                  name="icon"
                  render={(msg) => (
                    <Typography variant="body2" color="error">
                      {msg}
                    </Typography>
                  )}
                />

                {/* Title */}
                <Field name="title">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      label="Title"
                      fullWidth
                      margin="normal"
                      error={Boolean(meta.touched && meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>

                {/* Description */}
                <Field name="description">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      label="Description"
                      fullWidth
                      multiline
                      rows={3}
                      margin="normal"
                      error={Boolean(meta.touched && meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>

                {/* Image Upload */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Upload Image
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const imageName = await uploadImage(file);
                      setFieldValue("image", imageName);
                      setPreviewUrl(IMAGE_URL + imageName);
                    }
                  }}
                />
                {previewUrl && (
                  <Box mt={2}>
                    <img
                      crossOrigin="anonymous"
                      src={previewUrl}
                      alt="Image Preview"
                      style={{ width: 140, borderRadius: 8 }}
                    />
                  </Box>
                )}
                <ErrorMessage
                  name="image"
                  render={(msg) => (
                    <Typography variant="body2" color="error">
                      {msg}
                    </Typography>
                  )}
                />

                {/* ✅ Select Field */}
                <Field name="linked">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      select
                      label="Linked"
                      fullWidth
                      margin="normal"
                      error={Boolean(meta.touched && meta.error)}
                      helperText={meta.touched && meta.error}
                    >
                      {/* {skinConditionDescriptions.map((item, i) => {
                        return (
                          <MenuItem key={i} value={item}>
                            {item}
                          </MenuItem>
                        );
                      })} */}
                    </TextField>
                  )}
                </Field>

                {/* Features Section */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Features
                </Typography>
                <Features
                  groupItems={values.features}
                  onChange={(updated) => setFieldValue("features", updated)}
                />
                <ErrorMessage
                  name="features"
                  render={(msg) => (
                    <Typography variant="body2" color="error">
                      {msg}
                    </Typography>
                  )}
                />

                {/* Submit */}
                <Box mt={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isSubmitting}
                  >
                    {initialData ? "Update Concern" : "Create Concern"}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>
      </Container>
    </DashboardLayout>
  );
}

export default AddConcern;
